import { Fragment, useMemo, useState, type ChangeEvent } from 'react';
import {
  useApplyKfoImportMutation,
  useDeleteKfoBindingMutation,
  useListKfoBindingsQuery,
  useListKfoImportsQuery,
  useListPtoProjectsQuery,
  usePreviewKfoImportMutation,
  type KfoImport,
  type KfoImportPreview,
  type KfoOlderSliceConflict,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Actions,
  Banner,
  DataTable,
  Muted,
  Panel,
  PanelTitle,
  Row,
  SmallSelect,
  Stat,
  Stats,
  TableWrap,
  Tag,
  apiStatus,
  errorText,
  fmtDate,
  fmtDateTime,
} from './ui';

/** Сколько импортов истории показывать до «Показать все». */
const HISTORY_LIMIT = 15;

/**
 * 409 «срез старее последнего применённого» (§12): `{code:"older_slice",
 * last_report_date, report_date}` — на верхнем уровне тела или внутри `error`.
 */
function olderSliceFrom(err: unknown): KfoOlderSliceConflict | null {
  if (apiStatus(err) !== 409) return null;
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const top = data as Record<string, unknown>;
  const inner =
    top.error && typeof top.error === 'object' ? (top.error as Record<string, unknown>) : null;
  const src = top.code === 'older_slice' ? top : inner?.code === 'older_slice' ? inner : null;
  if (!src) return null;
  const pick = (k: string): string | null => {
    const v = src[k] ?? top[k];
    return typeof v === 'string' ? v : null;
  };
  return {
    code: 'older_slice',
    last_report_date: pick('last_report_date'),
    report_date: pick('report_date'),
  };
}

/**
 * Загрузка среза Заказчика (`.xlsb`/`.xlsx` книги «БД КФО»): разбор →
 * привязка договоров к проектам ПТО → загрузка. Срез общий на подрядчика:
 * договоры раскладываются по проектам, привязка запоминается. Здесь же
 * история импортов и список привязок.
 */
export function KfoImportTab({ projectId, canWrite }: { projectId: string; canWrite: boolean }) {
  const { data: projectsData } = useListPtoProjectsQuery();
  const projects = useMemo(
    () => (projectsData?.items ?? []).filter((p) => !p.deleted_at),
    [projectsData],
  );

  const [preview, { isLoading: previewing }] = usePreviewKfoImportMutation();
  const [applyImport, { isLoading: applying }] = useApplyKfoImportMutation();

  const [file, setFile] = useState<File | null>(null);
  const [pv, setPv] = useState<KfoImportPreview | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [result, setResult] = useState<KfoImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Сброс <input type="file"> после загрузки (поле неуправляемое).
  const [inputKey, setInputKey] = useState(0);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
    setPv(null);
    setResult(null);
    setError(null);
  }

  async function onPreview() {
    if (!file) return;
    setError(null);
    setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await preview(form).unwrap();
      setPv(res);
      setChoice(
        Object.fromEntries(
          res.contracts.map((c) => [
            c.contract_no,
            c.binding_project_id ?? c.suggested_project_id ?? '',
          ]),
        ),
      );
    } catch (e) {
      setError(
        errorText(
          e,
          'Не удалось разобрать файл. Нужна книга «БД КФО» Заказчика (.xlsb или .xlsx) с листами «КФО», «RFI», «ГО».',
        ),
      );
    }
  }

  async function onApply(force = false) {
    if (!file || !pv) return;
    setError(null);
    // §12: в `bindings` — ВСЕ договоры предпросмотра; «— не загружать —» =
    // `project_id: null`. Договор, которого в списке нет, сервер загрузил бы по
    // сохранённой привязке (а без прав на тот проект — 403 на весь срез).
    const bindings = pv.contracts.map((c) => ({
      contract_no: c.contract_no,
      project_id: choice[c.contract_no] || null,
    }));
    if (!bindings.some((b) => b.project_id)) {
      setError('Не выбран ни один проект: строки загружать некуда.');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    form.append('bindings', JSON.stringify(bindings));
    if (force) form.append('force', 'true');
    try {
      const res = await applyImport({ form, force }).unwrap();
      setResult(res);
      setPv(null);
      setFile(null);
      setInputKey((k) => k + 1);
    } catch (e) {
      const older = force ? null : olderSliceFrom(e);
      if (older) {
        const ok = window.confirm(
          `Срез от ${fmtDate(older.report_date) || '(дата не указана)'} старее уже загруженного ` +
            `от ${fmtDate(older.last_report_date) || '(дата не указана)'}. ` +
            'Числа Заказчика в строках откатятся к более старым. Всё равно загрузить?',
        );
        if (ok) await onApply(true);
        else setError('Загрузка отменена: в проектах уже есть более свежий срез.');
        return;
      }
      setError(errorText(e, 'Не удалось загрузить срез.'));
    }
  }

  const knownProjects = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);
  const boundCount = pv ? pv.contracts.filter((c) => choice[c.contract_no]).length : 0;
  const toThisProject = pv
    ? pv.contracts.filter((c) => choice[c.contract_no] === projectId).length
    : 0;

  return (
    <div>
      {canWrite && (
        <Panel>
          <PanelTitle>Загрузить срез Заказчика</PanelTitle>
          <Muted style={{ marginBottom: 12 }}>
            Книга «БД КФО» от Заказчика (.xlsb или .xlsx). Данные Заказчика в ОСНОВЕ не правятся —
            каждый новый срез обновляет строки, RFI и ГО, а изменившиеся числа попадают в историю
            строки. Наши отчёты и письма не затрагиваются.
          </Muted>
          <Actions>
            <input key={inputKey} type="file" accept=".xlsb,.xlsx" onChange={onFile} />
            <Button type="button" disabled={!file || previewing} onClick={onPreview}>
              {previewing ? 'Разбираю…' : 'Разобрать файл'}
            </Button>
          </Actions>
          {error && (
            <Banner $tone="danger" style={{ marginTop: 12 }}>
              {error}
            </Banner>
          )}

          {pv && (
            <div style={{ marginTop: 16 }}>
              <Stats>
                <Stat>
                  <b>{pv.contractor || '—'}</b>
                  <span>подрядчик</span>
                </Stat>
                <Stat>
                  <b>{fmtDate(pv.report_date) || '—'}</b>
                  <span>дата среза</span>
                </Stat>
                <Stat>
                  <b>{pv.kfo_rows.toLocaleString('ru-RU')}</b>
                  <span>строк КФО</span>
                </Stat>
                <Stat>
                  <b>{pv.rfi_rows.toLocaleString('ru-RU')}</b>
                  <span>записей RFI</span>
                </Stat>
                <Stat>
                  <b>{pv.go_rows.toLocaleString('ru-RU')}</b>
                  <span>записей ГО</span>
                </Stat>
              </Stats>
              {pv.rows_without_contract.rfi + pv.rows_without_contract.guarantees > 0 && (
                <Banner $tone="warning" style={{ marginBottom: 8 }}>
                  В файле есть записи без № договора и без строки КФО с тем же Ключом — они не
                  загрузятся: RFI — {pv.rows_without_contract.rfi.toLocaleString('ru-RU')}, ГО —{' '}
                  {pv.rows_without_contract.guarantees.toLocaleString('ru-RU')}.
                </Banner>
              )}
              <Muted style={{ marginBottom: 8 }}>
                Укажите, в какой проект ПТО загружать каждый договор. Один договор — один проект;
                выбор запомнится для следующих срезов. «— не загружать —» пропускает договор только
                в этот раз: запомненная привязка остаётся, строки в её проекте не меняются.
              </Muted>
              <TableWrap $maxHeight="40vh">
                <DataTable>
                  <thead>
                    <tr>
                      <th>№ договора</th>
                      <th className="num">Строк КФО</th>
                      <th className="num">RFI</th>
                      <th className="num">ГО</th>
                      <th>Проект ПТО</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pv.contracts.map((c) => {
                      const chosen = choice[c.contract_no] ?? '';
                      const rebind =
                        c.binding_project_id && chosen && chosen !== c.binding_project_id;
                      // Выбран проект не из списка пользователя (привязку делал коллега):
                      // показываем его, а не «не загружать», — иначе в state одно, на экране другое.
                      const foreign = !!projectsData && !!chosen && !knownProjects.has(chosen);
                      return (
                        <tr key={c.contract_no}>
                          <td className="nowrap">{c.contract_no}</td>
                          <td className="num">{c.lines.toLocaleString('ru-RU')}</td>
                          <td className="num">{c.rfi.toLocaleString('ru-RU')}</td>
                          <td className="num">{c.guarantees.toLocaleString('ru-RU')}</td>
                          <td>
                            <SmallSelect
                              value={chosen}
                              onChange={(e) =>
                                setChoice((p) => ({ ...p, [c.contract_no]: e.target.value }))
                              }
                              style={{ maxWidth: 340 }}
                            >
                              <option value="">— не загружать —</option>
                              {foreign && (
                                <option value={chosen}>
                                  {(chosen === c.binding_project_id && c.binding_project_name) ||
                                    'проект'}{' '}
                                  (нет в вашем списке)
                                </option>
                              )}
                              {projects.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.code ? `${p.code} · ` : ''}
                                  {p.name}
                                  {p.id === projectId ? ' (этот проект)' : ''}
                                </option>
                              ))}
                            </SmallSelect>{' '}
                            {c.binding_project_id && chosen === c.binding_project_id && (
                              <Tag $tone="success">запомнено</Tag>
                            )}
                            {c.binding_project_id && !chosen && (
                              <Tag
                                title={`Привязка к «${c.binding_project_name ?? c.binding_project_id}» сохранится; строки договора там не изменятся`}
                              >
                                в этот раз пропуск
                              </Tag>
                            )}
                            {foreign && (
                              <Tag
                                $tone="warning"
                                title="Загрузить в проект, которого нет в вашем списке, можно только с правом записи в него. Иначе выберите «— не загружать —»."
                              >
                                нет доступа?
                              </Tag>
                            )}
                            {!c.binding_project_id &&
                              c.suggested_project_id &&
                              chosen === c.suggested_project_id && (
                                <Tag $tone="info">по договору проекта</Tag>
                              )}
                            {rebind && (
                              <Tag
                                $tone="warning"
                                title={`Было: «${c.binding_project_name ?? c.binding_project_id}». Там строки договора пометятся «нет в срезе», RFI и ГО Заказчика по нему удалятся. Нужно право записи в оба проекта.`}
                              >
                                привязка изменится
                              </Tag>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </TableWrap>
              <Row style={{ marginTop: 12, marginBottom: 0 }}>
                <Muted>
                  Загрузить договоров: {boundCount} из {pv.contracts.length}
                  {toThisProject === 0 ? ' · в этот проект — ни одного' : ''}
                </Muted>
                <Button
                  type="button"
                  disabled={applying || boundCount === 0}
                  onClick={() => onApply()}
                >
                  {applying ? 'Загружаю…' : 'Загрузить срез'}
                </Button>
              </Row>
            </div>
          )}

          {result && (
            <div style={{ marginTop: 16 }}>
              <Banner $tone="success">
                Срез загружен: {result.source_filename}
                {result.report_date ? ` от ${fmtDate(result.report_date)}` : ''}.
              </Banner>
              <ImportStats imp={result} />
            </div>
          )}
        </Panel>
      )}

      <ImportHistory />
      <Bindings canWrite={canWrite} />
    </div>
  );
}

/** Предупреждения импорта (§12): договоры не из файла, перепривязки, строки без договора. */
function ImportWarnings({ imp }: { imp: KfoImport }) {
  const missing = imp.stats.contracts_missing ?? [];
  const rebound = imp.stats.rebound ?? [];
  const noContract = imp.stats.rows_without_contract;
  const noRfi = noContract?.rfi ?? 0;
  const noGo = noContract?.guarantees ?? 0;
  if (missing.length === 0 && rebound.length === 0 && noRfi + noGo === 0) return null;
  return (
    <Banner $tone="warning" style={{ marginTop: 8 }}>
      {missing.length > 0 && (
        <div>
          Привязанных договоров нет в этом файле ({missing.length}):{' '}
          {missing
            .map((m) => `${m.contract_no} («${m.project_name}», строк ${m.lines.toLocaleString('ru-RU')})`)
            .join(', ')}
          . Проверьте, тот ли срез загружен.
        </div>
      )}
      {rebound.map((r) => (
        <div key={`${r.contract_no}-${r.from_project_id}`}>
          Договор {r.contract_no} перенесён из проекта «{r.from_project_name}»:
          там помечено «нет в срезе» строк — {r.lines_marked_absent.toLocaleString('ru-RU')}, RFI и
          ГО Заказчика по договору удалены.
        </div>
      ))}
      {noRfi + noGo > 0 && (
        <div>
          Без № договора (и без строки КФО с тем же Ключом) не загружены: RFI —{' '}
          {noRfi.toLocaleString('ru-RU')}, ГО — {noGo.toLocaleString('ru-RU')}.
        </div>
      )}
    </Banner>
  );
}

function ImportStats({ imp }: { imp: KfoImport }) {
  return (
    <>
      <ImportWarnings imp={imp} />
      <TableWrap $maxHeight="40vh">
        <DataTable>
          <thead>
            <tr>
              <th>№ договора</th>
              <th>Проект</th>
              <th className="num">Строк</th>
              <th className="num">Новых</th>
              <th className="num">Обновлено</th>
              <th className="num">Нет в срезе</th>
              <th className="num">Изменений чисел</th>
              <th className="num">RFI</th>
              <th className="num">ГО</th>
              <th className="num">Наших RFI принято</th>
            </tr>
          </thead>
          <tbody>
            {imp.stats.contracts.map((c) => (
              <tr key={c.contract_no}>
                <td className="nowrap">{c.contract_no}</td>
                <td>{c.project_name}</td>
                <td className="num">{c.lines.toLocaleString('ru-RU')}</td>
                <td className="num">{c.new.toLocaleString('ru-RU')}</td>
                <td className="num">{c.updated.toLocaleString('ru-RU')}</td>
                <td className="num">{c.absent.toLocaleString('ru-RU')}</td>
                <td className="num">{c.changes.toLocaleString('ru-RU')}</td>
                <td className="num">{c.rfi.toLocaleString('ru-RU')}</td>
                <td className="num">{c.guarantees.toLocaleString('ru-RU')}</td>
                <td className="num">{c.matched_osnova_rfi.toLocaleString('ru-RU')}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </TableWrap>
      {imp.stats.unbound.length > 0 && (
        <Muted style={{ marginTop: 8 }}>
          Пропущены (не загружать / без проекта):{' '}
          {imp.stats.unbound
            .map((u) => `${u.contract_no} — ${u.lines.toLocaleString('ru-RU')} строк`)
            .join('; ')}
        </Muted>
      )}
    </>
  );
}

function ImportHistory() {
  const { data, isLoading, error } = useListKfoImportsQuery();
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const imports = data ?? [];
  const shown = all ? imports : imports.slice(0, HISTORY_LIMIT);

  return (
    <Panel>
      <PanelTitle>История загрузок</PanelTitle>
      {isLoading && <Muted>Загрузка…</Muted>}
      {error && <Banner $tone="danger">{errorText(error, 'Не удалось загрузить историю.')}</Banner>}
      {!isLoading && !error && imports.length === 0 && <Muted>Срезы ещё не загружались.</Muted>}
      {shown.length > 0 && (
        <TableWrap $maxHeight="50vh">
          <DataTable>
            <thead>
              <tr>
                <th>Загружен</th>
                <th>Файл</th>
                <th>Подрядчик</th>
                <th>Дата среза</th>
                <th className="num">Договоров</th>
                <th className="num">Строк</th>
                <th className="num">Изменений</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((imp) => {
                const lines = imp.stats.contracts.reduce((s, c) => s + c.lines, 0);
                const changes = imp.stats.contracts.reduce((s, c) => s + c.changes, 0);
                const isOpen = open === imp.id;
                return (
                  <Fragment key={imp.id}>
                    <tr data-click="true" onClick={() => setOpen(isOpen ? null : imp.id)}>
                      <td className="nowrap">{fmtDateTime(imp.created_at)}</td>
                      <td className="wrap">{imp.source_filename}</td>
                      <td>{imp.contractor}</td>
                      <td className="nowrap">{fmtDate(imp.report_date)}</td>
                      <td className="num">{imp.stats.contracts.length}</td>
                      <td className="num">{lines.toLocaleString('ru-RU')}</td>
                      <td className="num">{changes.toLocaleString('ru-RU')}</td>
                      <td>{isOpen ? '▾' : '▸'}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} style={{ background: 'transparent' }}>
                          <ImportStats imp={imp} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
      {imports.length > HISTORY_LIMIT && (
        <Button
          $size="sm"
          $variant="ghost"
          type="button"
          style={{ marginTop: 8 }}
          onClick={() => setAll((v) => !v)}
        >
          {all ? 'Свернуть' : `Показать все (${imports.length})`}
        </Button>
      )}
    </Panel>
  );
}

function Bindings({ canWrite }: { canWrite: boolean }) {
  const { data, isLoading, error } = useListKfoBindingsQuery();
  const [remove, { isLoading: removing }] = useDeleteKfoBindingMutation();
  const [err, setErr] = useState<string | null>(null);
  const bindings = data ?? [];

  async function onDelete(id: string, contractNo: string) {
    if (
      !window.confirm(
        `Забыть привязку договора ${contractNo}? Загруженные строки останутся; при следующем срезе проект нужно будет выбрать заново.`,
      )
    ) {
      return;
    }
    setErr(null);
    try {
      await remove(id).unwrap();
    } catch (e) {
      setErr(errorText(e, 'Не удалось удалить привязку.'));
    }
  }

  return (
    <Panel>
      <PanelTitle>Привязки договоров к проектам</PanelTitle>
      {isLoading && <Muted>Загрузка…</Muted>}
      {error && (
        <Banner $tone="danger">{errorText(error, 'Не удалось загрузить привязки.')}</Banner>
      )}
      {err && <Banner $tone="danger">{err}</Banner>}
      {!isLoading && !error && bindings.length === 0 && (
        <Muted>Привязок нет — они появятся при первой загрузке среза.</Muted>
      )}
      {bindings.length > 0 && (
        <TableWrap $maxHeight="40vh">
          <DataTable>
            <thead>
              <tr>
                <th>№ договора</th>
                <th>Проект ПТО</th>
                <th>С</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => (
                <tr key={b.id}>
                  <td className="nowrap">{b.contract_no}</td>
                  <td>{b.project_name}</td>
                  <td className="nowrap">{fmtDateTime(b.created_at)}</td>
                  {canWrite && (
                    <td>
                      <Button
                        $size="sm"
                        $variant="ghost"
                        type="button"
                        disabled={removing}
                        onClick={() => onDelete(b.id, b.contract_no)}
                      >
                        Забыть
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
    </Panel>
  );
}
